import { Award, BookOpen, Briefcase, Target, Eye, Heart, Users, TrendingUp, Sparkles } from "lucide-react";
import { Card } from "@/components/ui/card";
import connieHeadshot from "@/assets/connie-kyes-myland-new.webp";
const About = () => {
  return <section className="py-20 bg-background">
      <div className="container px-4 md:px-6">
        <div className="max-w-5xl mx-auto">
          <div>
            <h2 className="text-3xl md:text-5xl font-bold text-heading mb-6">Your Guide to Leadership Excellence</h2>
            <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
              Glow Up LEADership Academy, founded by Connie Kyes-Myland, helps professionals and organizations grow
              stronger leaders, improve communication, and build high-performing teams. We don't believe in
              cookie-cutter training — we design customized programs that align with your goals and deliver measurable
              results.
            </p>

            <h3 className="text-2xl font-semibold text-heading mt-12 mb-8 text-center">The Team</h3>

            <div className="my-16 flex flex-col items-center text-center">
              <img src={connieHeadshot} alt="Connie Kyes-Myland - Leadership Coach and Consultant" width="800" height="800" loading="lazy" decoding="async" className="max-w-2xl w-full max-h-96 object-contain rounded-lg mb-6" />
              <h3 className="text-2xl font-semibold text-heading mb-4">
                Experience, Expertise, and a Passion for Growth
              </h3>

              <div className="text-left max-w-3xl space-y-6">
                <div>
                  <h4 className="text-xl font-semibold text-heading mb-2">Meet Connie, CEO/Founder:</h4>
                  <p className="text-muted-foreground leading-relaxed">
                    With over 25 years of experience in sales and leadership, I've served as an individual contributor,
                    sales coach, trainer, and mid-level manager. My journey has taken me from mentoring entrepreneurs as
                    a Women's Business Development mentor to consulting with small and mid-sized businesses and managing
                    global enterprise accounts.
                  </p>
                </div>

                <div>
                  <h4 className="text-xl font-semibold text-heading mb-2">Professional Background:</h4>
                  <p className="text-muted-foreground leading-relaxed">
                    I've partnered with accounting and finance professionals, supported nonprofits, and worked alongside
                    organizations in technology, finance, and manufacturing. My expertise spans human resources, the
                    office of the CFO, and operational strategy — helping leaders and teams navigate complexity with
                    clarity and confidence.
                  </p>
                </div>

                <div>
                  <h4 className="text-xl font-semibold text-heading mb-2">Certifications & Approach:</h4>
                  <p className="text-muted-foreground leading-relaxed">
                    As a Maxwell Leadership Certified Coach, Trainer, and Speaker — and a Maxwell DISC Certified
                    Consultant specializing in interviewing and benchmarking — I integrate proven leadership principles
                    with behavioral insights. Through coaching, workshops, DISC impact reports, and organizational
                    agreements, I deliver practical tools that strengthen communication, align teams, and drive
                    measurable results.
                  </p>
                </div>

                <div>
                  <h4 className="text-xl font-semibold text-heading mb-2">Personal Mission:</h4>
                  <p className="text-muted-foreground leading-relaxed">
                    I believe leadership is influence — and influence grows when led with strength, strategy, and heart.
                    My mission is to help leaders, teams, and organizations reconnect with purpose, strengthen
                    communication, and achieve significance in both business and life.
                  </p>
                </div>
            </div>
          </div>

          <div className="my-16 text-center">
            <h3 className="text-2xl font-semibold text-heading mb-4">Meet Karen, CCO/CMO</h3>
            <div className="text-left max-w-3xl mx-auto space-y-6">
              <p className="text-muted-foreground leading-relaxed">
                Experienced communications professional with more than 15 years in corporate communications. Skilled in developing and implementing multi-channel content strategies, overseeing brand positioning, and ensuring consistent messaging across platforms to enhance visibility and engagement. Expertise includes managing corporate communications collateral, analyzing performance metrics, and optimizing strategies to improve ROI.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Committed to purpose-driven messaging, actively collaborating with cross-functional teams to support mission-driven brands. Proficient in translating complex concepts into clear, impactful language, enabling teams to deliver value through targeted marketing efforts. Dedicated to creating innovative, scalable communication strategies that align with organizational goals and foster meaningful connections with diverse audiences.
              </p>
            </div>
          </div>

          <div className="my-16 text-center">
            <h3 className="text-2xl font-semibold text-heading mb-4">Meet Narcisa, CTO </h3>
            <div className="text-left max-w-3xl mx-auto space-y-6">
              <p className="text-muted-foreground leading-relaxed">
                V. Narcisa Budworth is an elite technology executive with a proven record of engineering transformation, AI-driven modernization, and global team scaling across fintech, capital markets, and secure communications platforms. Equal parts strategist and technologist, she leads organizations that deliver high-velocity innovation, operational resilience, and enterprise-grade security.
              </p>
              
              <p className="text-muted-foreground leading-relaxed">
                She previously worked for Morgan Stanley, Symphony Communications, PEAK6 Investments, Rewards Network, and OptionsHouse, where she consistently built high-performing global engineering teams, modernized large-scale platforms, strengthened automation and DevOps ecosystems, and delivered secure, resilient, AI-ready technology environments.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Narcisa is known for elevating engineering culture, accelerating innovation, and driving scalable, intelligent systems that set new standards of excellence.
              </p>
            </div>
          </div>

          <h3 className="text-2xl font-semibold text-heading mb-8">
            Supporting Leaders, Teams, and Organizations Across Every Stage
          </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="p-6 hover:shadow-card-hover transition-all duration-300 border-border/50">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 bg-[#fdb12d]">
                  <Award className="w-6 h-6 text-primary" />
                </div>
                <h4 className="font-semibold text-heading mb-2">Individuals</h4>
                <p className="text-muted-foreground text-sm">​Sales professional, solopreneurs, career seekers.</p>
              </Card>

              <Card className="p-6 hover:shadow-card-hover transition-all duration-300 border-border/50">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 bg-[#fdb12d]">
                  <Briefcase className="w-6 h-6 text-primary" />
                </div>
                <h4 className="font-semibold text-heading mb-2">Leaders</h4>
                <p className="text-muted-foreground text-sm">
                  New and experienced leaders building influence and resilience.
                </p>
              </Card>

              <Card className="p-6 hover:shadow-card-hover transition-all duration-300 border-border/50">
                <div className="w-12 h-12 rounded-lg bg-[#fdb12d] flex items-center justify-center mb-4">
                  <BookOpen className="w-6 h-6 text-primary" />
                </div>
                <h4 className="font-semibold text-heading mb-2">Teams</h4>
                <p className="text-muted-foreground text-sm">
                  Groups needing stronger communication, trust, and alignment.
                </p>
              </Card>

              <Card className="p-6 hover:shadow-card-hover transition-all duration-300 border-border/50">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 bg-[#fdb12d]">
                  <BookOpen className="w-6 h-6 text-primary" />
                </div>
                <h4 className="font-semibold text-heading mb-2">Organizations</h4>
                <p className="text-muted-foreground text-sm">
                  Companies ready to transform culture, productivity, and leadership pipelines.
                </p>
              </Card>
            </div>

            <h3 className="text-2xl font-semibold text-heading mt-12 mb-8">Our Purpose and Principles</h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="p-6 hover:shadow-card-hover transition-all duration-300 border-border/50">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 bg-[#fdb12d]">
                  <Target className="w-6 h-6 text-primary" />
                </div>
                <h4 className="font-semibold text-heading mb-3 text-xl">Mission</h4>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Glow Up LEADership Academy empowers organizations, leaders, and individual contributors to cultivate
                  essential leadership, communication, and personal growth skills — igniting purpose, passion, and
                  confidence so they can boldly let their light shine.
                </p>
              </Card>

              <Card className="p-6 hover:shadow-card-hover transition-all duration-300 border-border/50">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 bg-[#fdb12d]">
                  <Eye className="w-6 h-6 text-primary" />
                </div>
                <h4 className="font-semibold text-heading mb-3 text-xl">Vision</h4>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  To be the leading leadership development academy for solopreneurs, sales professionals, and emerging
                  leaders — cultivating confident, capable, and purpose-driven individuals who create meaningful impact
                  in their communities and across the globe.
                </p>
              </Card>

              <Card className="p-6 hover:shadow-card-hover transition-all duration-300 border-border/50">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 bg-[#fdb12d]">
                  <Heart className="w-6 h-6 text-primary" />
                </div>
                <h4 className="font-semibold text-heading mb-3 text-xl">Core Values</h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#fdb12d]" />
                    <span className="text-muted-foreground text-sm">Authenticity</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-[#fdb12d]" />
                    <span className="text-muted-foreground text-sm">Empowerment</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-[#fdb12d]" />
                    <span className="text-muted-foreground text-sm">Growth</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#fdb12d]" />
                    <span className="text-muted-foreground text-sm">Community</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Award className="w-4 h-4 text-[#fdb12d]" />
                    <span className="text-muted-foreground text-sm">Excellence</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Heart className="w-4 h-4 text-[#fdb12d]" />
                    <span className="text-muted-foreground text-sm">Purpose</span>
                  </div>
                </div>
              </Card>
            </div>

            <h3 className="text-2xl font-semibold text-heading mt-12 mb-6">Real Growth. Real Impact.</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="p-6 hover:shadow-card-hover transition-all duration-300 border-border/50">
                <h4 className="text-xl font-semibold text-heading mb-3">Leadership Development & Growth</h4>
                <p className="text-primary font-semibold mb-2">Grow Leaders Who Inspire and Deliver</p>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Whether you're an aspiring leader, a seasoned executive, or a business owner, leadership growth is
                  essential. We help leaders step into new responsibilities, coach their teams effectively, and prepare
                  for succession — building organizations that thrive today and tomorrow.
                </p>
              </Card>

              <Card className="p-6 hover:shadow-card-hover transition-all duration-300 border-border/50">
                <h4 className="text-xl font-semibold text-heading mb-3">Communication & Influence</h4>
                <p className="text-primary font-semibold mb-2">Master the Art of Connection</p>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Strong communication drives influence. From one-on-one conversations to public speaking, we equip
                  leaders and teams to listen deeply, resolve conflict, and connect with audiences. You'll learn to
                  inspire trust, deliver powerful presentations, and influence clients with confidence.
                </p>
              </Card>

              <Card className="p-6 hover:shadow-card-hover transition-all duration-300 border-border/50">
                <h4 className="text-xl font-semibold text-heading mb-3">Organizational Culture & Performance</h4>
                <p className="text-primary font-semibold mb-2">Build Teams That Work — and Win</p>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Healthy culture fuels performance. We help organizations break down silos, strengthen collaboration,
                  and embed values like trust, diversity, and inclusion. Whether forming new teams, navigating change,
                  or recovering from setbacks, we guide leaders to create environments where people succeed together.
                </p>
              </Card>

              <Card className="p-6 hover:shadow-card-hover transition-all duration-300 border-border/50">
                <h4 className="text-xl font-semibold text-heading mb-3">Purpose, Passion & Creativity</h4>
                <p className="text-primary font-semibold mb-2">Lead with Meaning and Vision</p>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Success isn't just about results — it's about significance. We help individuals and creatives
                  reconnect with their passions, overcome imposter syndrome, and find fulfillment in their work. By
                  aligning purpose with performance, you'll lead a life and career that truly matter.
                </p>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
export default About;